import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { api, getUser } from '@/utils/api';
import { ArrowLeft, CheckCircle, AlertTriangle } from 'lucide-react';
import { format, differenceInDays, parseISO } from 'date-fns';
import DemoBanner from '@/components/DemoBanner';
import useDemoExpiry from '@/hooks/useDemoExpiry';

const STAGES = [
  { key: 'replacement_prepared', label: 'Replacement Prepared' },
  { key: 'replacement_dispatched', label: 'Replacement Dispatched' },
  { key: 'delivered_to_customer', label: 'Delivered to Customer' },
  { key: 'qc_done_at_customer_site', label: 'QC Done at Customer Site' },
  { key: 'old_item_deinstalled', label: 'Old Item Deinstalled' },
  { key: 'old_item_picked_up', label: 'Old Item Picked Up' },
  { key: 'old_item_received_at_warehouse', label: 'Old Item Received at Warehouse' },
];

const TABLETOP_DAYS = [3, 11, 11, 11, 17, 17, 17];
const FRAME_DAYS = [8, 8, 8, 14, 14, 14, 14];

const WorkOrderDetail = () => {
  const navigate = useNavigate();
  const { workOrderId } = useParams();
  const user = getUser();
  useDemoExpiry();
  const isDemoMode = user?.role === 'demo';
  const [workOrder, setWorkOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingStage, setUpdatingStage] = useState(null);

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'demo')) {
      navigate('/admin/login');
      return;
    }
    fetchWorkOrder();
  }, [workOrderId]);

  const fetchWorkOrder = async () => {
    try {
      const res = await api.get(`/work-orders/${workOrderId}`);
      setWorkOrder(res.data);
    } catch {
      toast.error('Failed to fetch work order');
    } finally {
      setLoading(false);
    }
  };

  const handleStageToggle = async (stageKey, currentDone) => {
    try {
      setUpdatingStage(stageKey);
      const res = await api.patch(`/work-orders/${workOrderId}/stage`, {
        stage_name: stageKey,
        done: !currentDone,
      });
      setWorkOrder(res.data);
      if (res.data.status === 'Completed') {
        toast.success('Work order completed — all stages done!');
      }
    } catch {
      toast.error('Failed to update stage');
    } finally {
      setUpdatingStage(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        Loading...
      </div>
    );
  }

  if (!workOrder) {
    return (
      <div className="min-h-screen bg-background">
        <nav className="border-b border-border bg-card/50 backdrop-blur-sm">
          <div className="container mx-auto px-6 py-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/work-orders')}
              data-testid="back-to-work-orders-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />Back to Work Orders
            </Button>
          </div>
        </nav>
        <div className="flex items-center justify-center min-h-[60vh]">
          Work order not found.
        </div>
      </div>
    );
  }

  const daysElapsed = differenceInDays(new Date(), parseISO(workOrder.created_at));
  const daysRemaining = differenceInDays(parseISO(workOrder.sla_deadline), new Date());
  const completedCount = STAGES.filter(
    (s) => workOrder.stages[s.key]?.done
  ).length;
  const guidanceDays =
    workOrder.replacement_type === 'Tabletop' ? TABLETOP_DAYS : FRAME_DAYS;

  const isGuidanceOverdue = (stageIndex) => {
    const stage = STAGES[stageIndex];
    return !workOrder.stages[stage.key]?.done && daysElapsed > guidanceDays[stageIndex];
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/work-orders')}
            data-testid="back-to-work-orders-btn"
            className="h-11"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />Back to Work Orders
          </Button>
        </div>
      </nav>
      <DemoBanner />

      <div className="container mx-auto px-4 sm:px-6 py-6 lg:py-8 max-w-2xl">
        {/* Completed banner */}
        {workOrder.status === 'Completed' && (
          <div
            className="mb-6 p-4 bg-green-100 border border-green-300 rounded-lg flex items-center gap-3"
            data-testid="work-order-complete-banner"
          >
            <CheckCircle className="h-5 w-5 text-green-700 flex-shrink-0" />
            <p className="text-green-800 font-semibold">
              Work Order Complete — All 7 stages done
            </p>
          </div>
        )}

        {/* Summary card */}
        <Card className="border-border shadow-lg mb-6" data-testid="work-order-summary-card">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-xl font-heading font-mono">
                  {workOrder.work_order_id}
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Linked Ticket:{' '}
                  <span className="font-mono">
                    {workOrder.parent_ticket_id.substring(0, 8)}
                  </span>
                </p>
              </div>
              <Badge
                className={
                  workOrder.status === 'Completed'
                    ? 'bg-green-100 text-green-800 border-green-200'
                    : 'bg-yellow-100 text-yellow-800 border-yellow-200'
                }
                data-testid="wo-detail-status-badge"
              >
                {workOrder.status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Customer</p>
                <p className="font-medium">{workOrder.customer_name}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Replacement Type</p>
                <Badge variant="outline">{workOrder.replacement_type}</Badge>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">SLA Deadline</p>
                <p className="font-medium">
                  {format(parseISO(workOrder.sla_deadline), 'PPP')}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Days Remaining</p>
                <p
                  className={`font-bold text-lg ${
                    daysRemaining < 0
                      ? 'text-red-600'
                      : daysRemaining <= 3
                      ? 'text-amber-600'
                      : 'text-green-700'
                  }`}
                  data-testid="wo-days-remaining"
                >
                  {daysRemaining < 0
                    ? `${Math.abs(daysRemaining)}d overdue`
                    : `${daysRemaining} days`}
                </p>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-2" data-testid="work-order-progress">
              <div className="flex justify-between text-sm mb-2">
                <span className="font-medium">Progress</span>
                <span className="text-muted-foreground">
                  {completedCount} of 7 stages complete
                </span>
              </div>
              <div className="h-3 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    completedCount === 7 ? 'bg-green-500' : 'bg-primary'
                  }`}
                  style={{ width: `${(completedCount / 7) * 100}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stages checklist */}
        <Card className="border-border shadow-lg" data-testid="stages-checklist">
          <CardHeader>
            <CardTitle className="text-lg font-heading">Stages Checklist</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {STAGES.map((stage, idx) => {
                const stageData = workOrder.stages[stage.key] || {
                  done: false,
                  done_at: null,
                };
                const guidanceOverdue = isGuidanceOverdue(idx);
                const isUpdating = updatingStage === stage.key;
                const guidanceDay = guidanceDays[idx];

                return (
                  <div
                    key={stage.key}
                    className={`flex items-start gap-4 p-3 rounded-lg border transition-colors min-h-[44px] ${
                      stageData.done
                        ? 'bg-green-50 border-green-200'
                        : guidanceOverdue
                        ? 'bg-red-50 border-red-200'
                        : 'bg-background border-border'
                    }`}
                    data-testid={`stage-row-${stage.key}`}
                  >
                    <input
                      type="checkbox"
                      checked={stageData.done}
                      onChange={() => !isDemoMode && handleStageToggle(stage.key, stageData.done)}
                      disabled={isUpdating || isDemoMode}
                      className={`h-5 w-5 mt-1 accent-primary flex-shrink-0 ${isDemoMode ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                      data-testid={`stage-checkbox-${stage.key}`}
                      title={isDemoMode ? 'Not available in demo' : undefined}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p
                          className={`text-sm font-medium ${
                            stageData.done
                              ? 'text-green-700 line-through opacity-70'
                              : guidanceOverdue
                              ? 'text-red-700'
                              : ''
                          }`}
                        >
                          {stage.label}
                        </p>
                        {guidanceOverdue ? (
                          <span className="text-xs text-red-500 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            Overdue (by day {guidanceDay})
                          </span>
                        ) : (
                          !stageData.done && (
                            <span className="text-xs text-muted-foreground">
                              (by day {guidanceDay})
                            </span>
                          )
                        )}
                      </div>
                      {stageData.done && stageData.done_at && (
                        <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          Done on {format(parseISO(stageData.done_at), 'PPp')}
                        </p>
                      )}
                    </div>
                    {isUpdating && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mt-0.5 flex-shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WorkOrderDetail;
